/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio

import models.SavingsGoal

/**
 * Sibling to [StudioOrchestrator] for the Debt Paydown Studio. Pure
 * (no I/O). Returns the pair of projections the comparison strip + chart
 * render: two editable plans (PLAN_A / PLAN_B) — by default PLAN_A is
 * seeded from the goal's stored fields and PLAN_B from the same baseline.
 * The user then diverges them via the lever sliders.
 */
object DebtPayoffOrchestrator {

    /** Per-plan slider state — one of these for each card. */
    data class SliderState(
        /** Monthly-equivalent payment. The projector converts to a per-period
         *  amount via [PaymentFrequency.periodPayment]. */
        val monthlyPayment: Double,
        /** Annual interest rate as a fraction (e.g. 0.0385). */
        val annualRate: Double,
        val lumpSum: Double = 0.0,
        val paymentFrequency: PaymentFrequency = PaymentFrequency.MONTHLY,
    )

    /** Studio-level state: both plans together. */
    data class StudioState(
        val planA: SliderState,
        val planB: SliderState,
    )

    /** Default sliders derived from the goal's stored debt fields.
     *
     *  Unit note: SavingsGoal.debtInterestRate is stored as a percent
     *  number (e.g. 4.85 means 4.85%) because the edit form's "%" input
     *  collects it that way. The projector and rate slider both work in
     *  fractions (0.0485), so we convert at this boundary.
     *
     *  If the goal has a previously-applied frequency / monthly-equivalent
     *  payment (from a past "Apply to goal" click), those take precedence
     *  over the amortizing-payment derivation. Otherwise both plans start
     *  at MONTHLY cadence with the standard amortizing payment for the
     *  goal's balance + remaining months.
     */
    fun defaultSliders(goal: SavingsGoal): StudioState {
        val base = baseSliderFromGoal(goal)
        return StudioState(planA = base, planB = base)
    }

    private fun baseSliderFromGoal(goal: SavingsGoal): SliderState {
        val balance = goal.debtRemainingBalance ?: 0.0
        val rate = (goal.debtInterestRate ?: 0.0) / 100.0
        val months = goal.debtRemainingMonths ?: 0
        val frequency = goal.debtPaymentFrequency ?: PaymentFrequency.MONTHLY
        val storedPayment = goal.debtMonthlyEquivalentPayment
        val payment = storedPayment ?: if (months > 0) {
            DebtPayoffProjector.amortizingPayment(balance, rate, months)
        } else {
            balance * 0.02  // credit-card-style 2 % minimum payment fallback
        }
        return SliderState(
            monthlyPayment = payment,
            annualRate = rate,
            lumpSum = 0.0,
            paymentFrequency = frequency,
        )
    }

    /** Run the comparison: returns PLAN_A (seeded from the goal) and PLAN_B
     *  (slider-controlled). */
    fun runComparison(goal: SavingsGoal, state: StudioState): List<DebtProjection> {
        val balance = goal.debtRemainingBalance ?: 0.0
        return listOf(
            DebtPayoffProjector.project(scenarioFrom(balance, state.planA, DebtScenarioKind.PLAN_A)),
            DebtPayoffProjector.project(scenarioFrom(balance, state.planB, DebtScenarioKind.PLAN_B)),
        )
    }

    private fun scenarioFrom(
        balance: Double,
        slider: SliderState,
        kind: DebtScenarioKind,
    ): DebtScenario = DebtScenario(
        kind = kind,
        initialBalance = balance,
        annualRate = slider.annualRate,
        monthlyPayment = slider.monthlyPayment,
        lumpSum = slider.lumpSum,
        paymentFrequency = slider.paymentFrequency,
    )
}

/** Top-level alias so callers can write `StudioState(...)` without the
 *  `DebtPayoffOrchestrator.` prefix. */
typealias StudioState = DebtPayoffOrchestrator.StudioState
